// File: src/components/SeriesManagement.js
// Series CRUD component

import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, MoreVertical, Trash2 } from 'lucide-react';
import { API_BASE_URL } from '../App';
import { useLanguage } from '../contexts/LanguageContext';
import { useCountry } from '../contexts/CountryContext';
import { formatSeriesNameWithYear } from '../utils/utils';
import { handleUnauthorized } from '../utils/session';
import CountryOptions from './CountryOptions';
import { getCountry } from '../data/countries';

function SeriesManagement({ series, selectedSeries, setSelectedSeries, currentSeries, token, loadSeries }) {
  const { t } = useLanguage();
  const { currentCountry } = useCountry();
  const [showModal, setShowModal] = useState(false);
  const [editingSeries, setEditingSeries] = useState(null);
  const [seriesForm, setSeriesForm] = useState({ name: '', startYear: '', endYear: '', country: currentCountry.code });
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMenu]);

  const createSeries = async () => {
    if (!seriesForm.name.trim()) {
      alert(t('seriesNameRequired'));
      return;
    }

    if (!seriesForm.country) {
      alert(t('countryRequired'));
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/series`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(seriesForm)
      });

      if (handleUnauthorized(response)) return;

      if (response.ok) {
        loadSeries();
        setShowModal(false);
        setSeriesForm({ name: '', startYear: '', endYear: '', country: currentCountry.code });
      } else {
        const data = await response.json().catch(() => ({}));
        alert(t('saveFailed') + (data.error ? `: ${data.error}` : ''));
      }
    } catch (error) {
      console.error('Error creating series:', error);
      alert(t('connectionError'));
    }
  };

  const updateSeries = async () => {
    if (!seriesForm.name.trim()) {
      alert(t('seriesNameRequired'));
      return;
    }

    if (!seriesForm.country) {
      alert(t('countryRequired'));
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/series/${editingSeries.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: seriesForm.name,
          startYear: seriesForm.startYear ? parseInt(seriesForm.startYear) : null,
          endYear: seriesForm.endYear ? parseInt(seriesForm.endYear) : null,
          country: seriesForm.country
        })
      });

      if (handleUnauthorized(response)) return;

      if (response.ok) {
        loadSeries();
        setShowModal(false);
        setEditingSeries(null);
        setSeriesForm({ name: '', startYear: '', endYear: '', country: currentCountry.code });
      } else {
        const data = await response.json().catch(() => ({}));
        alert(t('saveFailed') + (data.error ? `: ${data.error}` : ''));
      }
    } catch (error) {
      console.error('Error updating series:', error);
      alert(t('connectionError'));
    }
  };

  const deleteSeries = async (id) => {
    if (!window.confirm(t('deleteThisSeries'))) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/series/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (handleUnauthorized(response)) return;
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(t('saveFailed') + (data.error ? `: ${data.error}` : ''));
        return;
      }
      loadSeries();
    } catch (error) {
      console.error('Error deleting series:', error);
      alert(t('connectionError'));
    }
  };

  return (
    <>
      <div className="bg-paper rounded-lg p-6 mb-6 border border-line">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-ink">{t('racingSeries')}</h2>

          {/* 3-Dots Menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="bg-paper-alt hover:opacity-80 text-ink p-2 rounded-lg"
              title={t('menu')}
            >
              <MoreVertical className="w-5 h-5" />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-2 bg-paper rounded-lg shadow-lg border border-line py-2 z-50 min-w-[200px]">
                {/* New Series */}
                <button
                  onClick={() => {
                    setShowModal(true);
                    setSeriesForm({ name: '', startYear: '', endYear: '', country: currentCountry.code });
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-paper-alt transition-colors flex items-center gap-2 text-ink-muted"
                >
                  <Plus className="w-4 h-4" />
                  {t('newSeries')}
                </button>

                {currentSeries && (
                  <>
                    {/* Edit Series */}
                    <button
                      onClick={() => {
                        setEditingSeries(currentSeries);
                        setSeriesForm({
                          name: currentSeries.name,
                          startYear: currentSeries.start_year || '',
                          endYear: currentSeries.end_year || '',
                          country: currentSeries.country || currentCountry.code
                        });
                        setShowModal(true);
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-paper-alt transition-colors flex items-center gap-2 text-ink-muted"
                    >
                      <Edit2 className="w-4 h-4" />
                      {t('editSeries')}
                    </button>

                    {/* Delete Series */}
                    <button
                      onClick={() => {
                        deleteSeries(currentSeries.id);
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-paper-alt transition-colors flex items-center gap-2 text-red-700 dark:text-red-400 border-t border-line"
                    >
                      <Trash2 className="w-4 h-4" />
                      {t('deleteSelectedSeries')}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {series.length > 0 ? (
          <select
            value={selectedSeries || ''}
            onChange={(e) => setSelectedSeries(parseInt(e.target.value))}
            className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
          >
            {series.map(s => (
              <option key={s.id} value={s.id}>
                {formatSeriesNameWithYear(s.name, s.start_year, s.end_year)}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-ink-muted">{t('noSeriesYet')}</p>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-paper rounded-lg p-6 max-w-md w-full border border-line">
            <h3 className="text-xl font-bold text-ink mb-4">
              {editingSeries ? t('editSeries') : t('newSeries')}
            </h3>
            <input
              type="text"
              placeholder={t('seriesName')}
              value={seriesForm.name}
              onChange={(e) => setSeriesForm({ ...seriesForm, name: e.target.value })}
              className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink mb-4"
            />

            <div className="mb-4">
              <label className="block text-ink-muted mb-2 text-sm">{t('country')} <span className="text-red-700 dark:text-red-500">*</span></label>
              <div className="relative">
                <select
                  value={seriesForm.country}
                  onChange={(e) => setSeriesForm({ ...seriesForm, country: e.target.value })}
                  className="w-full px-4 py-2 pl-12 bg-paper-alt border border-line rounded text-ink appearance-none cursor-pointer"
                  required
                >
                  <CountryOptions withFlag={false} />
                </select>
                {/* Flag icon positioned absolutely */}
                <span
                  className={`fi ${getCountry(seriesForm.country)?.flagClass || ''} absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none`}
                  style={{ fontSize: '1.5rem' }}
                ></span>
                {/* Dropdown arrow */}
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-muted pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-ink-muted mb-2 text-sm">{t('startYear')}</label>
                <input
                  type="number"
                  placeholder="2024"
                  value={seriesForm.startYear}
                  onChange={(e) => setSeriesForm({ ...seriesForm, startYear: e.target.value })}
                  className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                  min="2000"
                  max="2100"
                />
              </div>
              <div>
                <label className="block text-ink-muted mb-2 text-sm">{t('endYear')} <span className="text-ink-muted">({t('optional')})</span></label>
                <input
                  type="number"
                  placeholder="2025"
                  value={seriesForm.endYear}
                  onChange={(e) => setSeriesForm({ ...seriesForm, endYear: e.target.value })}
                  className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                  min="2000"
                  max="2100"
                />
              </div>
            </div>
            <p className="text-sm text-ink-muted mb-4">💡 {t('yearHint')}</p>
            <div className="flex gap-3">
              <button
                onClick={editingSeries ? updateSeries : createSeries}
                className="flex-1 bg-accent hover:opacity-90 text-accent-ink py-2 rounded font-semibold"
              >
                {editingSeries ? t('update') : t('create')}
              </button>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingSeries(null);
                  setSeriesForm({ name: '', startYear: '', endYear: '', country: currentCountry.code });
                }}
                className="flex-1 bg-paper-alt hover:opacity-80 text-ink py-2 rounded font-semibold"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SeriesManagement;